import React, { useEffect, useMemo, useState } from "react";
import { HolderOutlined, SettingOutlined } from "@ant-design/icons";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Checkbox, Modal, Space } from "antd";
import "./grid-settings.css";

/**
 * @typedef {{ key: string, title?: React.ReactNode, label?: string }} GridSettingsColumn
 * @typedef {{ order: string[], hidden: string[] }} GridSettingsValue
 */

/** @param {GridSettingsColumn[]} columns */
export function defaultGridSettings(columns) {
  const keys = (columns || []).map((c) => c.key).filter(Boolean);
  return { order: keys, hidden: [] };
}

/** @param {string} storageKey */
export function loadGridSettings(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const order = Array.isArray(parsed?.order)
      ? parsed.order.filter((k) => typeof k === "string")
      : [];
    const hidden = Array.isArray(parsed?.hidden)
      ? parsed.hidden.filter((k) => typeof k === "string")
      : [];
    return { order, hidden };
  } catch {
    return null;
  }
}

/** @param {string} storageKey @param {GridSettingsValue} value */
export function saveGridSettings(storageKey, value) {
  if (!storageKey || !value) return;
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        order: value.order || [],
        hidden: value.hidden || []
      })
    );
  } catch {
    /* ignore */
  }
}

/**
 * Сливает сохранённые настройки с актуальным набором колонок:
 * неизвестные ключи отбрасываются, новые — в конец, видимы.
 * @param {GridSettingsColumn[]} columns
 * @param {GridSettingsValue | null | undefined} settings
 * @returns {GridSettingsValue}
 */
export function normalizeGridSettings(columns, settings) {
  const keys = (columns || []).map((c) => c.key).filter(Boolean);
  const keySet = new Set(keys);
  const order = [];
  const seen = new Set();

  for (const key of settings?.order || []) {
    if (!keySet.has(key) || seen.has(key)) continue;
    order.push(key);
    seen.add(key);
  }
  for (const key of keys) {
    if (seen.has(key)) continue;
    order.push(key);
    seen.add(key);
  }

  const hidden = (settings?.hidden || []).filter((key) => keySet.has(key));
  return { order, hidden };
}

/**
 * Применяет порядок и видимость к определениям колонок таблицы.
 * @param {Array<{ key: string }>} columns
 * @param {GridSettingsValue | null | undefined} settings
 */
export function applyGridSettings(columns, settings) {
  const list = Array.isArray(columns) ? columns : [];
  const normalized = normalizeGridSettings(list, settings);
  const byKey = new Map(list.map((col) => [col.key, col]));
  const hidden = new Set(normalized.hidden);
  const result = [];

  for (const key of normalized.order) {
    const col = byKey.get(key);
    if (!col || hidden.has(key)) continue;
    result.push(col);
  }
  return result;
}

/**
 * Объединяет новые настройки текущего набора колонок с ранее сохранёнными
 * (чтобы не терять колонки другого режима / экрана).
 * @param {GridSettingsValue | null | undefined} previous
 * @param {GridSettingsValue} next
 * @param {GridSettingsColumn[]} currentColumns
 */
export function mergeGridSettings(previous, next, currentColumns) {
  const currentKeys = new Set((currentColumns || []).map((c) => c.key).filter(Boolean));
  const order = [];
  const seen = new Set();

  for (const key of next?.order || []) {
    if (!key || seen.has(key)) continue;
    order.push(key);
    seen.add(key);
  }
  for (const key of previous?.order || []) {
    if (!key || seen.has(key) || currentKeys.has(key)) continue;
    order.push(key);
    seen.add(key);
  }

  const nextHidden = new Set(next?.hidden || []);
  const prevHidden = new Set(previous?.hidden || []);
  const hidden = order.filter((key) =>
    currentKeys.has(key) ? nextHidden.has(key) : prevHidden.has(key)
  );

  return { order, hidden };
}

const columnLabel = (col) => {
  if (col?.label != null && col.label !== "") return String(col.label);
  if (typeof col?.title === "string" || typeof col?.title === "number") {
    return String(col.title);
  }
  return String(col?.key ?? "");
};

const SortableRow = ({ id, label, visible, onToggleVisible }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.85 : undefined
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`grid-settings__row${isDragging ? " grid-settings__row--dragging" : ""}`}
    >
      <button
        type="button"
        className="grid-settings__handle"
        title="Перетащить"
        aria-label={`Перетащить: ${label}`}
        {...attributes}
        {...listeners}
      >
        <HolderOutlined />
      </button>
      <Checkbox
        className="grid-settings__check"
        checked={visible}
        onChange={(e) => onToggleVisible(id, e.target.checked)}
      >
        {label}
      </Checkbox>
    </li>
  );
};

/**
 * Кнопка «Настройка» + диалог видимости и порядка колонок.
 * Настройки сохраняются в localStorage по storageKey.
 */
const GridSettings = ({
  columns = [],
  storageKey,
  value,
  onChange,
  /** Сброс к этим настройкам (порядок + hidden). Если не задано — все колонки видимы. */
  defaultSettings = null,
  title = "Настройка таблицы",
  buttonProps
}) => {
  const [open, setOpen] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);
  const [draftHidden, setDraftHidden] = useState(() => new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 }
    })
  );

  const columnMap = useMemo(() => {
    const map = new Map();
    (columns || []).forEach((col) => {
      if (col?.key) map.set(col.key, col);
    });
    return map;
  }, [columns]);

  const resolved = useMemo(
    () => normalizeGridSettings(columns, value),
    [columns, value]
  );

  useEffect(() => {
    if (!open) return;
    setDraftOrder(resolved.order.slice());
    setDraftHidden(new Set(resolved.hidden));
  }, [open, resolved]);

  const openModal = () => setOpen(true);

  const closeModal = () => setOpen(false);

  const commit = (next) => {
    const normalized = normalizeGridSettings(columns, next);
    const previous = value || (storageKey ? loadGridSettings(storageKey) : null);
    const merged = mergeGridSettings(previous, normalized, columns);
    if (storageKey) saveGridSettings(storageKey, merged);
    onChange?.(merged);
  };

  const onOk = () => {
    commit({ order: draftOrder, hidden: [...draftHidden] });
    closeModal();
  };

  const onReset = () => {
    const defaults = normalizeGridSettings(
      columns,
      defaultSettings || defaultGridSettings(columns)
    );
    setDraftOrder(defaults.order.slice());
    setDraftHidden(new Set(defaults.hidden));
  };

  const toggleVisible = (key, checked) => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftOrder((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  return (
    <>
      <Button
        icon={<SettingOutlined />}
        title={title}
        aria-label={title}
        onClick={openModal}
        {...buttonProps}
      />
      <Modal
        title={title}
        open={open}
        onCancel={closeModal}
        width={420}
        destroyOnHidden
        className="grid-settings-modal"
        rootClassName="grid-settings-modal"
        footer={
          <div className="grid-settings__footer">
            <Space>
              <Button type="primary" onClick={onOk}>
                ОК
              </Button>
              <Button onClick={onReset}>По умолчанию</Button>
            </Space>
            <Button onClick={closeModal}>Отмена</Button>
          </div>
        }
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={draftOrder} strategy={verticalListSortingStrategy}>
            <ul className="grid-settings__list">
              {draftOrder.map((key) => {
                const col = columnMap.get(key);
                if (!col) return null;
                return (
                  <SortableRow
                    key={key}
                    id={key}
                    label={columnLabel(col)}
                    visible={!draftHidden.has(key)}
                    onToggleVisible={toggleVisible}
                  />
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      </Modal>
    </>
  );
};

export default GridSettings;
